// Two fixes for the KwikPass account area at /pages/kp-account/*.
//
// KwikPass mounts its account UI in a shadow root attached to #MainContent.
// Everything else about it is reachable from CSS — its design tokens are inline
// custom properties on the host, and custom properties inherit through a shadow
// boundary, so kashira-theme.css handles the colours and fonts there.
//
// These two are not reachable that way, and both were confirmed by measurement
// rather than assumed:
//
//   1. The host is painted #E1E2E3, a grey belonging to no palette on this
//      site. KwikPass's own --kp-color-background is already #FBF8F1, so this
//      is a separate hardcoded fill. Setting background-color on #MainContent
//      from our stylesheet does nothing — even an inline `!important` on the
//      element loses, because an important declaration inside the shadow tree
//      beats an important declaration from the outer document when they target
//      the host. The only way to win is to declare it inside the shadow tree.
//
//   2. The container carries `md:max-w-[1400px]`, a Tailwind class compiled
//      into the shadow stylesheet with no variable behind it. Our page width is
//      1600px, so the account area sits 200px narrower than every other page.
//
// Injecting a stylesheet into another app's shadow root is reaching into its
// internals, so this is written to fail quietly rather than loudly: the shadow
// root is `open` by KwikPass's own choice, the two selectors are the outermost
// and most stable ones they have, and if either stops matching the page simply
// keeps their styling. Nothing here throws and nothing blocks rendering.
//
// Polling, deliberately, not a MutationObserver: attachShadow does NOT produce
// a mutation record, so an observer on the light tree can never see the shadow
// root appear, and everything KwikPass renders afterwards happens inside that
// shadow tree where the observer is not looking. The first draft of this file
// used an observer and silently never fired. Polling is bounded and clears
// itself on success — an earlier script on this project (the Judge.me
// histogram) hung the page by watching without ever stopping.
(function () {
  'use strict';

  var STYLE_ID = 'kashira-kwikpass-overrides';
  var POLL_MS = 200;
  var TIMEOUT_MS = 20000;
  var poll = null;
  var timer = null;

  // Uses the theme's own --page-width so this tracks the page-width setting
  // instead of hardcoding a second copy of it.
  //
  // The profile fields are the third thing CSS cannot reach from outside. They
  // ship as underline-only rules (`border-b border-[#8E9FBC]`, 25px tall, blue
  // focus) which is not this site's field style and fails two of its rules: a
  // 25px control is under the 44px minimum touch target, and #8E9FBC is not a
  // boundary tone here. This restates the theme's own field contract —
  // --kashira-field-rest-border rather than --kashira-field-border, because
  // the latter reads 1.4:1 on warm grounds and WCAG 1.4.11 wants 3:1 for a
  // control's edge (see THE FIELD STATE SYSTEM in kashira-theme.css).
  //
  // Selector weights are deliberate: `.kp-main-container input` is (0,1,1) and
  // beats their `border-b` at (0,1,0); the focus rule is (0,2,1) and beats
  // `focus:border-blue-500` at (0,2,0). No !important needed for either.
  // NOTE ON TOKEN TYPES. --kashira-ivory/cream/hairline are HEX; maroon,
  // ink-soft, ink-faint and gold-deep are RGB TRIPLETS and must be wrapped in
  // rgb(). Getting this wrong does not fall back to the var() default — the
  // declaration is invalid at computed-value time and is dropped silently.
  // That is precisely how --kp-color-disabled shipped broken.
  var CSS = [
    // Ground. Panels sit on cream and are drawn on the paper ivory, which is
    // the pairing the rest of the site uses; before this everything was one
    // flat ivory and the panels had no edge at all.
    ':host { background-color: var(--kashira-cream, #f4eee1) !important; }',
    '.kp-main-container { max-width: var(--page-width, 160rem) !important; }',

    '.side-nav, .main-content {',
    '  background-color: var(--kashira-ivory, #fbf8f1);',
    '  border: 1px solid var(--kashira-hairline, #efe9da);',
    '  border-radius: 4px;',
    '}',

    // Section rules, main panel only. .kp-title is not reserved for section
    // headings — the sidebar uses it for "Account" and for every nav item's
    // label, so scoping this to .kp-main-container underlined all of them.
    '.main-content .kp-title {',
    '  border-bottom: 1px solid var(--kashira-hairline, #efe9da);',
    '  padding-bottom: 0.8rem;',
    '}',

    '.side-nav button {',
    '  border-top: 1px solid var(--kashira-hairline, #efe9da);',
    '  padding-top: 1.2rem;',
    '}',

    // Text fields only. The earlier version of this rule matched every input,
    // so the "default address" checkbox was handed a 46px min-height, 1.4rem
    // padding and a field border — which is the box drawn around it.
    ".kp-main-container input:not([type='checkbox']):not([type='radio']) {",
    '  background-color: var(--kashira-field-bg, #fffdf8);',
    '  border: var(--inputs-border-width, 1px) solid',
    '    var(--kashira-field-rest-border, rgba(43, 41, 37, 0.75));',
    '  border-radius: var(--inputs-radius, 2px);',
    '  min-height: 4.6rem;',
    '  padding: 0 1.4rem;',
    '  font-size: 1.4rem;',
    '}',

    // One ring, not two. The theme's own contract is outline-offset 0 with the
    // border taken to transparent, so the ring REPLACES the border. Keeping the
    // border coloured and offsetting the outline drew both at once.
    ".kp-main-container input:not([type='checkbox']):not([type='radio']):focus,",
    ".kp-main-container input:not([type='checkbox']):not([type='radio']):focus-visible {",
    '  outline: 0.2rem solid rgb(var(--kashira-gold-deep, 142 98 56));',
    '  outline-offset: 0;',
    '  border-color: transparent;',
    '  box-shadow: none;',
    '}',

    // Disabled fields carry text-kp-disabled but read as ordinary values, so
    // nothing signals that phone and email cannot be edited here.
    //
    // The :not() chain is repeated deliberately. Without it this rule is
    // (0,2,1) and the base field rule above is (0,3,1) — :not() takes its
    // argument's specificity, so the two attribute selectors add (0,2,0) — and
    // the base wins on background and border while colour and cursor still
    // apply, giving a half-styled field that looks like a bug in the app.
    ".kp-main-container input:not([type='checkbox']):not([type='radio']):disabled {",
    '  background-color: var(--kashira-cream, #f4eee1);',
    '  border-color: var(--kashira-hairline, #efe9da);',
    '  color: rgb(var(--kashira-ink-faint, 113 108 98));',
    '  cursor: not-allowed;',
    '}',

    // Checkbox, styled as one rather than as a text field.
    ".kp-main-container input[type='checkbox'] {",
    '  accent-color: rgb(var(--kashira-maroon, 100 37 46));',
    '  width: 1.8rem;',
    '  height: 1.8rem;',
    '  min-height: 0;',
    '  padding: 0;',
    '  border-radius: var(--inputs-radius, 2px);',
    '}',

    ".kp-main-container input[type='checkbox']:focus-visible {",
    '  outline: 0.2rem solid rgb(var(--kashira-gold-deep, 142 98 56));',
    '  outline-offset: 0.2rem;',
    '}',

    // Primary buttons ("Save Address"). Their rest state is token-driven and
    // already maroon via bg-kp-primary, but the hover is the hardcoded Tailwind
    // `hover:bg-blue-700` — so the button flipped from maroon to blue under the
    // cursor. maroon-700 is this theme's documented hover tone for maroon.
    // Weight: (0,3,0) against their (0,2,0). The radius and label ride along,
    // since rounded-lg is 8px against this theme's 2px and text-white is not
    // the on-dark tone the rest of the site uses on maroon.
    '.kp-main-container .bg-kp-primary {',
    '  border-radius: var(--inputs-radius, 2px);',
    '  color: var(--kashira-on-dark, #f9f4e9);',
    '}',

    '.kp-main-container .bg-kp-primary:hover {',
    '  background-color: rgb(var(--kashira-maroon-700, 79 29 37));',
    '}',

    '.kp-main-container .bg-kp-primary:focus-visible {',
    '  outline: 0.2rem solid rgb(var(--kashira-gold-deep, 142 98 56));',
    '  outline-offset: 0.2rem;',
    '}',

    // Three colours in this UI bypass KwikPass's own token system as Tailwind
    // arbitrary values, so neither the GoKwik dashboard nor the custom-property
    // overrides in kashira-theme.css can reach them:
    //   border-[#8E9FBC] x4  field borders  (handled by the input rule above)
    //   text-[#5A6D8C]  x4  field labels
    //   text-[#1750BA]  x1  the Update link
    // Matching the class is the only hook. It is brittle by nature, but fails
    // safely: change the hex their end and the rule simply stops applying.
    '.kp-main-container .text-\\[\\#5A6D8C\\] {',
    '  color: rgb(var(--kashira-ink-soft, 87 83 74));',
    '}',

    '.kp-main-container .text-\\[\\#1750BA\\] {',
    '  color: rgb(var(--kashira-maroon, 100 37 46));',
    '}',
  ].join('\n');

  function stop() {
    if (poll) {
      clearInterval(poll);
      poll = null;
    }
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function apply() {
    var host = document.getElementById('MainContent');
    if (!host || !host.shadowRoot) return false;

    // Idempotent: re-running must never stack duplicate sheets.
    if (host.shadowRoot.getElementById(STYLE_ID)) {
      stop();
      return true;
    }

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    host.shadowRoot.appendChild(style);
    stop();
    return true;
  }

  // ---- icon tinting: attempted, and it does not work from here ------------
  //
  // The account icons are <img> pointing at monochrome SVGs on GoKwik's CDN
  // (pdp.gokwik.co/kp-account/assets/icons/*.svg). An <img> paints its own
  // pixels, so no colour property of ours reaches them.
  //
  // The obvious route is the one this theme already uses for its alert glyph:
  // mask the element with the icon so only its ALPHA is read, paint the shape
  // with background-color, blank the raster. Implemented and MEASURED here on
  // 2026-08-14: all twelve icons reported the right background and a mask, and
  // every one of them rendered INVISIBLE. Mask images are subject to CORS, and
  // these are cross-origin with no permissive header, so the mask resolves to
  // nothing and masks the element away entirely.
  //
  // CSS filters are the only same-origin-free alternative and can merely
  // approximate a target colour through an invert/sepia/saturate/hue-rotate
  // chain, which is not something to hand a brand palette to.
  //
  // The two real fixes, neither of which belongs in this file:
  //   1. GoKwik ship these as inline SVG using currentColor, or serve them
  //      with Access-Control-Allow-Origin. This is the seventh item for them.
  //   2. We ship our own same-origin icon set and swap the src — fully
  //      controllable, and removes a third-party request per icon.
  //
  // Left undone deliberately rather than shipped as an approximation.

  function start() {
    if (apply()) return;

    // The shadow root is attached asynchronously, once KwikPass's bundle has
    // loaded and its merchant-status check has returned. Poll for it, and give
    // up rather than running forever if the app never mounts.
    poll = setInterval(apply, POLL_MS);
    timer = setTimeout(stop, TIMEOUT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
